import React, { useState } from 'react';
import { Colors, Dropdown, Menu } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import styled from 'styled-components/macro';
import { notification } from 'antd';
import { gql, useApolloClient } from '@apollo/client';
import { deleteRulesetMutation } from 'utils/data-validation-graphql';
import { GQLOperations, Ruleset } from 'types/dt-graphql-apollo-v2';
import { term } from 'utils/project-ruleset';
import { ConfirmationDialog } from 'components';
import { useAccessControl } from 'hooks/useAccessControl';

export const RulesetOptions = ({
  ruleset,
  onEditRuleset,
  onDeleteRulesetComplete,
}: {
  ruleset: Ruleset;
  onEditRuleset: (ruleset: Ruleset) => void;
  onDeleteRulesetComplete: VoidFunction;
}) => {
  const apolloClient = useApolloClient();
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const hasAccess = useAccessControl();
  const canEditRuleset = hasAccess(['Update_Ruleset']);
  const canDeleteRuleset = hasAccess(['Delete_Ruleset']);

  const onDeleteRuleset = async () => {
    try {
      const query = deleteRulesetMutation(ruleset);
      await apolloClient.mutate({
        mutation: gql(query),
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      onDeleteRulesetComplete();
    } catch (err) {
      notification.error({
        message: `Could not delete ${term.ruleset}`,
        description: err?.toString(),
      });
    }
  };

  const onCompleteDelete = async () => {
    setLoadingDelete(true);
    await onDeleteRuleset();
    setConfirmDeleteDialog(false);
    setLoadingDelete(false);
  };

  const onHandleDeleteRuleset = (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setConfirmDeleteDialog(true);
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      aria-hidden="true"
      role="link"
    >
      {(canEditRuleset || canDeleteRuleset) && (
        <Dropdown
          content={
            <StyledOptionsMenu>
              {canEditRuleset && (
                <Menu.Item
                  onClick={(
                    e:
                      | React.MouseEvent<HTMLDivElement, MouseEvent>
                      | React.MouseEvent<HTMLElement, MouseEvent>
                  ) => {
                    e.stopPropagation();
                    onEditRuleset(ruleset);
                  }}
                  icon="Edit"
                  iconPlacement="left"
                >
                  <span>Edit {term.ruleset}</span>
                </Menu.Item>
              )}
              {canDeleteRuleset && (
                <Menu.Item
                  onClick={onHandleDeleteRuleset}
                  icon="Delete"
                  iconPlacement="left"
                  destructive
                >
                  <span>Delete {term.ruleset} from project</span>
                </Menu.Item>
              )}
            </StyledOptionsMenu>
          }
        >
          <StyledOptionsIcon type="EllipsisVertical" size={18} />
        </Dropdown>
      )}

      {confirmDeleteDialog && (
        <ConfirmationDialog
          title={`Delete ${term.ruleset}`}
          message={`Are you sure you want to delete the ${term.ruleset} "${ruleset.name}"?`}
          textOk={`Yes, delete ${term.ruleset}`}
          buttonTypeOk="destructive"
          onOk={() => onCompleteDelete()}
          width={400}
          onClose={() => setConfirmDeleteDialog(false)}
          loading={loadingDelete}
        />
      )}
    </div>
  );
};

const StyledOptionsMenu = styled(Menu)`
  min-width: 100px !important;
`;

const StyledOptionsIcon = styled(Icon)`
  &:hover {
    cursor: pointer;
    background: var(--cogs-bg-accent);
  }
`;

const StyledDeleteOption = styled(Menu.Item)`
  color: var(--cogs-danger);
`;
